import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSubscription } from '../../context/SubscriptionContext';
import useSubscriptionApi from '../../hooks/useSubscriptionApi';
import useCountdownRedirect from '../../hooks/useCountdownRedirect';
import { SUPPORT_EMAIL } from '../../utils/constants';
import '../../styles/SubscriptionSuccess.css';

/**
 * SubscriptionCancel Component
 *
 * Page shown when user cancels the checkout process.
 * Features:
 * - Shows current subscription status (if any)
 * - Offers trial for new users
 * - Auto-redirects to plans page
 */
const SubscriptionCancel = () => {
    const { t } = useTranslation('subscription');
    const navigate = useNavigate();
    const location = useLocation();

    const {
        subscriptionData,
        canStartTrial,
        refreshSubscriptionStatus,
        getSubscriptionStatusText,
        formatDate
    } = useSubscription();

    const { startTrial, error: apiError, clearError } = useSubscriptionApi();

    const [isStartingTrial, setIsStartingTrial] = useState(false);
    const [error, setError] = useState(null);

    // Countdown redirect to plans page
    const { countdown } = useCountdownRedirect('/subscription/plans', 10);

    // Refresh subscription and log cancelled session
    useEffect(() => {
        const sessionId = new URLSearchParams(location.search).get('session_id');
        if (sessionId) {
            console.log('Checkout cancelled:', sessionId);
        }

        refreshSubscriptionStatus();
    }, [location, refreshSubscriptionStatus]);

    /**
     * Handle starting trial
     */
    const handleStartTrial = async () => {
        try {
            setIsStartingTrial(true);
            setError(null);
            clearError();

            await startTrial();
            await refreshSubscriptionStatus();
            navigate('/dashboard');
        } catch (err) {
            setError(
                apiError ||
                err.message ||
                t('api.startTrialFailed')
            );
        } finally {
            setIsStartingTrial(false);
        }
    };

    /**
     * Handle contact support
     */
    const handleContactSupport = () => {
        const subject = encodeURIComponent(t('cancelPage.emailSubject'));
        const body = encodeURIComponent(t('cancelPage.emailBody'));
        window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
    };

    const hasActiveSubscription = subscriptionData?.isActive;
    const showTrialOption = canStartTrial();

    return (
        <div className="subscription-result-container">
            <div className="result-card cancel">
                <div className="icon">×</div>

                <h1>{t('cancelPage.title')}</h1>

                <p className="message">
                    {t('cancelPage.noCharges')}
                    {hasActiveSubscription
                        ? ` ${t('cancelPage.subscriptionActive')}`
                        : ` ${t('cancelPage.tryAgainOrContact')}`}
                </p>

                {/* Current subscription info */}
                <CurrentStatusSection
                    subscriptionData={subscriptionData}
                    getStatusText={getSubscriptionStatusText}
                    formatDate={formatDate}
                />

                {/* Trial offer for new users */}
                {showTrialOption && (
                    <TrialOfferSection
                        isStartingTrial={isStartingTrial}
                        onStartTrial={handleStartTrial}
                    />
                )}

                {/* Error message */}
                {error && (
                    <div className="error-message">
                        <p>{error}</p>
                    </div>
                )}

                {/* Help section */}
                <HelpSection onContactSupport={handleContactSupport} />

                <p className="redirect-info">
                    {t('cancelPage.redirecting', { seconds: countdown })}
                </p>

                <div className="actions">
                    <button
                        className="primary-btn"
                        onClick={() => navigate('/subscription/plans')}
                    >
                        {t('choosePlan')}
                    </button>
                    <button
                        className="secondary-btn"
                        onClick={() => navigate('/dashboard')}
                    >
                        {t('cancelPage.backToDashboard')}
                    </button>
                </div>
            </div>
        </div>
    );
};

/**
 * Current subscription status section
 */
const CurrentStatusSection = ({ subscriptionData, getStatusText, formatDate }) => {
    const { t } = useTranslation('subscription');
    if (!subscriptionData || subscriptionData.status === 'NONE') {
        return null;
    }

    return (
        <div className="current-status">
            <h3>{t('status.title')}</h3>
            <p>
                <strong>{t('expired.status')}</strong> {getStatusText(subscriptionData.status)}
            </p>
            {subscriptionData.endDate && (
                <p>
                    <strong>{t('cancelPage.endDate')}</strong> {formatDate(subscriptionData.endDate)}
                </p>
            )}
            {subscriptionData.daysRemaining > 0 && (
                <p>
                    <strong>{t('statusCard.daysRemainingLabel')}</strong> {subscriptionData.daysRemaining}
                </p>
            )}
        </div>
    );
};

/**
 * Trial offer section
 */
const TrialOfferSection = ({ isStartingTrial, onStartTrial }) => {
    const { t } = useTranslation('subscription');
    return (
        <div className="trial-offer">
            <h3>🎁 {t('cancelPage.trialTitle')}</h3>
            <p>
                {t('cancelPage.trialMessage')}
            </p>
            <button
                className="trial-btn"
                onClick={onStartTrial}
                disabled={isStartingTrial}
            >
                {isStartingTrial ? t('cancelPage.activating') : t('cancelPage.startFree')}
            </button>
        </div>
    );
};

/**
 * Help section
 */
const HelpSection = ({ onContactSupport }) => {
    const { t } = useTranslation('subscription');
    return (
        <div className="help-section">
            <p>
                {t('cancelPage.helpQuestion')}
            </p>
            <button className="support-btn" onClick={onContactSupport}>
                {t('cancelPage.contactUs')}
            </button>
        </div>
    );
};

export default SubscriptionCancel;
